import { compareMeetingSessionRoles, isSpectator, MeetingOrderedMedia, useOrderedMeetingMedia } from "@kalyzee/kast-web-sdk";
import { Meeting, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import { debounce } from "lodash";
import { useCallback, useMemo, useRef } from "react";
import { PageSlaveMode } from "../../components/navigation/PageContext";
import { ScreenConstants } from "../../constants/screens";
import { SessionMode } from "../../contexts/appStore/hooks";
import { ClientContainersIteration } from "../../helpers/bridge/common";
import { MessagingBridgeManagerMasterClient } from "../../helpers/bridge/master";
import { matchEngine } from "../../helpers/matchEngine";
import { SecondaryScreenLayout } from "../../interfaces/secondaryScreens";
import { useSettings } from "../settings";

export const DEFAULT_CAROUSEL_INTERVAL = 60000;
export const DEFAULT_CAROUSEL_DELAY = 5000;

const roundSplit = <T> (
  items: T[], 
  screens: { capacity: number, rounds: { enabled: boolean, items: T[] }[] }[],
) => {
  let currRoundIndex = 0;
  let currScreenIndex = 0;

  const pushItem = (item: T): boolean => {
    const currScreen = screens[currScreenIndex];
    let currRound = currScreen.rounds.length > currRoundIndex ? currScreen.rounds[currRoundIndex] : undefined;
    if (currRound === undefined) {
      currRound = { enabled: true, items: [] };
      currScreen.rounds.push(currRound);
    } 
    if (currRound.items.length >= currScreen.capacity) {
      if (currScreenIndex + 1 >= screens.length) {
        currScreenIndex = 0;
        currRoundIndex ++;
        return pushItem(item);;
      }
      currScreenIndex++;
      return pushItem(item);
    }
    currRound.items.push(item);
    return true;
  }

  items.forEach((i) => {
    pushItem(i);
  });
};

////////////////////////////////////////////////
//////////////////// MASTER ////////////////////
////////////////////////////////////////////////

interface Group {
  id: string,
  capacity: number,
  rounds: { enabled: boolean, items: MeetingOrderedMedia[] }[],
  roundIndex: number, 
  nextRoundIndex?: number,
}

interface Carousel {
  id: string,
  media: MeetingOrderedMedia[],
  connectedMedia: MeetingOrderedMedia[],
  groups: Group[],
}

interface MainCarousel {
  media: MeetingOrderedMedia[],
  connectedMedia: MeetingOrderedMedia[],
  displayNextCarousel?: Date,
  displayNextCarouselTimeout?: NodeJS.Timer,
  /*
    1. The videos are split in groups
    2. Containers will define a video group
    3. For each video group, the videos are split among containers,
    taking their respective capacity into account. If there is not enough
    capacity, multiple rounds are created.

    In other words :
    2 containers with the same video group id will display different videos,
    in a temporal carousel fashion way.
  */
  carousels: Carousel[],
}

////////////////////////////////////////////////
//////////////////// CLIENT ////////////////////
////////////////////////////////////////////////

// Represents a column within a client,
// linked to a specific video carousel and video group
interface ClientContainer {
  id: string, // refers to the specific column id in the layout
  client: MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>,
  carouselId: string,
  groupId: string,
  capacity: number,
}

export const useVideoCarousel = ({
  meeting,
  session,
  getClients,
  onCarousel,
}: {
  meeting?: Meeting,
  session?: MeetingSession,
  getClients: () => MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[],
  onCarousel: (data: ClientContainersIteration, delay: number, id: string) => void,
}) => {
  const [settings] = useSettings();

  const orderedMedia = useOrderedMeetingMedia(meeting, useCallback((_, s) => {
    if (!session) return false;
    if (isSpectator(s.role)) return false;
    return s.id !== session?.id;
  }, [session]));

  
  const carouselIntervalRef = useRef<NodeJS.Timer>();
  const carouselInfoRef = useRef<MainCarousel>();
  
  const sessionsMappedById = useMemo(() => {
    const map: { [key: string]: MeetingSession } = {};
    meeting?.sessions?.forEach((s) => {
      map[s.id] = s;
    });
    return map;
  }, [meeting?.sessions]);

  // A little map of layoutId -> layout
  const layoutsMap: { [key: number]: SecondaryScreenLayout | undefined } = useMemo( 
    () => Object.fromEntries( 
      (settings.carousel?.layouts ?? [])?.map(layout => ([ 
        layout.id,
        layout,
      ])),
    ), 
    [settings], 
  ); 

  const getLayout = useCallback((layoutId: number | undefined) => {
    // Note : it's possible and allowed to provide an "undefined" layoutId in the settings
    // So here if layoutId is undefined, we still try to get the layout with id=undefined
    let layout = layoutsMap[layoutId as any];
    if (layout) return layout;

    // perhaps we were given an invalid layoutId
    // we try the fallback if there is one
    layout = layoutsMap[undefined as any];
    if (layout) return layout;

    return ScreenConstants.DefaultLayout;
  }, [layoutsMap]);

  // Extracts the video containers from a client
  const getClientContainers = useCallback((
    client: MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>
  ): ClientContainer[] => {
    const layoutId = client.data?.data?.layoutId;
    const layout = getLayout(layoutId);

    const containers: ClientContainer[] = [];
    layout.videoContainers.forEach((container, idx) => {
      containers.push({
        client,
        id: container.id,
        // Default behavior is that all columns belong to the same carousel :
        carouselId: container.carouselId ?? "default",
        // Default behavior is that each player column has its own group
        // So we assign them with unique IDs :
        groupId: container.groupId ?? `${client.id}-${idx}`,
        capacity: container.capacity,
      });
    });

    return containers;
  }, [getLayout]);

  const convertToPageInfo = (idx: number, rounds: Group['rounds']): {
    page: number, 
    pageCount: number,
    round?: Group['rounds'][number],
  } => {
    let page = -1;
    let pageCount = 0;
    let selectedRound: Group['rounds'][number] | undefined = undefined;

    // we'll try to get back on the index we were before,
    // if not possible, we go to the next available page after this index,
    // if there is none, we loop back to the first available page
    //
    // if page are 0,1,0,[0],1
    // then we'll get 0,1,0,0,[1]
    //
    // if page are 0,1,0,[0],0
    // then we'll get 0,[1],0,0,0
    rounds.forEach((round, roundIdx) => {
      if (round.enabled) {
        if (roundIdx < idx) {
          // we are under ideal index, we continue going
          page++;
        } else {
          // we are at ideal index or above
          if (!selectedRound) {
            // now, we need to pick the first enabled round after the ideal index
            selectedRound = round;
            page++;
          }
        }
        pageCount++;
      }
    });
    
    return { page: selectedRound ? page : 0, pageCount, round: selectedRound };
  };

  /**
   *  Carousel :
   *    Group :
   *      ClientContainer[]
   * There can be multiple ClientContainer per client.
   * A ClientContainer represents a column in a client layout, bound to a carousel and a group.
   */
  const getClientContainersMappedByCarouselAndGroup = useCallback((): Map<string, Map<string, ClientContainer[]>>  => {
    const clients = getClients();
    const map = new Map<string, Map<string, ClientContainer[]>>();

    clients.forEach((client) => {
      const containers = getClientContainers(client);

      containers.forEach((clientContainer) => {
        const { carouselId, groupId } = clientContainer;
  
        let carousel = map.get(carouselId);
        if (!carousel) {
          carousel = new Map<string, ClientContainer[]>();
          map.set(carouselId, carousel);
        }
  
        let group = carousel.get(groupId);
        if (!group) {
          group = [];
          carousel.set(groupId, group);
        }
  
        group.push(clientContainer);
      });
    });

    return map;
  }, [getClients, getClientContainers]);

  const generateCarouselInfo = useCallback(() => {
    const media = orderedMedia;
    const previousCarouselInfo = carouselInfoRef.current;
    const newCarouselData: MainCarousel = { 
      media: media,
      connectedMedia: media.filter(m => m.connected),
      displayNextCarousel: previousCarouselInfo?.displayNextCarousel,
      displayNextCarouselTimeout: previousCarouselInfo?.displayNextCarouselTimeout,
      carousels: [],
    };
    carouselInfoRef.current = newCarouselData;

    const containersByCarouselAndGroup = getClientContainersMappedByCarouselAndGroup();

    containersByCarouselAndGroup.forEach((containersByGroup, carouselId) => {
      const customFilter = settings.carousel?.filters?.find(f => f.carouselId === carouselId)?.value;

      let filteredMedia = media;
      if (customFilter) {
        filteredMedia = media.filter((media) => {
          const freshSession = sessionsMappedById[media.session.id];
          if (!freshSession) return false;

          const freshMedia = freshSession.medias.find(m => m.id === media.media.id);
          if (!freshMedia) return false;

          return matchEngine(
            {
              session: freshSession,
              media: freshMedia,
            }, 
            customFilter, 
            {
              "session.role": (a, b) => -compareMeetingSessionRoles(a, b),
            },
          );
        });
      }

      // First we take care of the groups, one by one
      const previousGroup = previousCarouselInfo?.carousels.find(c => c.id === carouselId);

      const groups: Group[] = [];

      containersByGroup.forEach((containers, groupId) => {
        if (!containers.length) return;

        // We retrieve the previous indexes
        const previousContainer = previousGroup?.groups.find(c => c.id === groupId);
        const roundIndex = previousContainer?.roundIndex ?? 0;
        const nextRoundIndex = previousContainer?.nextRoundIndex;

        const group: Group = {
          id: groupId,
          capacity: 0,
          rounds: [],
          roundIndex,
          nextRoundIndex,
        };

        // Many containers can belong to the same group,
        // capacity is the minimum capacity of all the containers in this group
        let capacity = Infinity;
        containers.forEach(container => {
          capacity = Math.min(capacity, container.capacity);
        });
        group.capacity = capacity;

        // A new group is created,
        // this group can be used in different places
        groups.push(group);
      });

      // We create the rounds for the containers of this group
      roundSplit<MeetingOrderedMedia>(filteredMedia, groups);

      // remove rounds if only disconnect user & round > 1
      groups.forEach((group) => {
        group.rounds.forEach((round) => {
          let containsOnlyDisconnectedUsers = true;
          for (let session of round.items) {
            if (session.connected) {
              containsOnlyDisconnectedUsers = false;
              break;
            }
          }
          if (containsOnlyDisconnectedUsers) {
            round.enabled = false;
          }
        });

        group.roundIndex = getValidRoundIndex(group, group.roundIndex);
        if (group.nextRoundIndex) group.nextRoundIndex = getValidRoundIndex(group, group.nextRoundIndex);
      });
      
      // A new group is created
      newCarouselData.carousels.push({
        id: carouselId,
        media: filteredMedia,
        connectedMedia: filteredMedia.filter(m => m.connected),
        groups,
      });
    });
  }, [getClientContainersMappedByCarouselAndGroup, orderedMedia, sessionsMappedById, settings.carousel?.filters]);

  const getValidRoundIndex = (container: Group, startedAt: number) => {
    if (!container.rounds.length) return 0;
    let currIndex = startedAt % container.rounds.length;
    const firstIndex = currIndex;
    let isValid = false;
    do {
      const round = container.rounds[currIndex];
      if (round.enabled) {
        isValid = true;
        break;
      }
      currIndex = (currIndex + 1) % container.rounds.length
    } while (currIndex !== firstIndex);
    return isValid ? currIndex : 0;
  }

  const setNext = useCallback((
    mode: 'increment' | 'restart', 
    delay: number = 0,
  ) => {
    const carouselInfo = carouselInfoRef.current;
    if (!carouselInfo) return;

    const containersByCarouselAndGroup = getClientContainersMappedByCarouselAndGroup();

    // Clients will receive their respective data,
    // Since multiple containers can exist per client, we gather all the data first
    const clientContainersIteration: Map<string, ClientContainersIteration> = new Map();
    const getOrCreate = (clientId: string): ClientContainersIteration => {
      let container = clientContainersIteration.get(clientId);
      if (!container) {
        container = { containers: [] };
        clientContainersIteration.set(clientId, container);
      }
      return container;
    }

    carouselInfo.carousels.forEach((carousel) => {
      const containersByGroup = containersByCarouselAndGroup.get(carousel.id);

      // Session Mode
      carousel.groups.forEach((group) => {
        const containers = containersByGroup?.get(group.id);
        
        if (!group.rounds.length) {
          containers?.forEach((container) => {
            const iteration = getOrCreate(container.client.id);
            iteration.containers.push({
              id: container.id, // column id
              sessions: [],
              totalSessions: 0,
              page: 0,
              pageCount: 0,
              carouselId: container.carouselId,
              groupId: container.groupId,
            });
          });
          return;
        }

        let nextRoundIndex = getValidRoundIndex(group, group.roundIndex);
        if (mode === "restart") nextRoundIndex =  getValidRoundIndex(group, 0);
        else if (mode === 'increment') nextRoundIndex = getValidRoundIndex(group, group.roundIndex + 1);

        // Setting next round
        group.nextRoundIndex = nextRoundIndex;

        // Session Mode
        containers?.forEach((container) => {
          const currCapacity = Math.min(group.capacity, container.capacity);
          const pageInfo = convertToPageInfo(group.nextRoundIndex ?? 0, group.rounds);

          const iteration = getOrCreate(container.client.id);
          iteration.containers.push({
            id: container.id, // column id
            sessions: pageInfo.round ? pageInfo.round.items.slice(0, currCapacity).map((s) => s.media) : [],
            totalSessions: carousel.connectedMedia.length,
            page: pageInfo.page,
            pageCount: pageInfo.pageCount,
            carouselId: container.carouselId,
            groupId: container.groupId,
          });
        });
      });
    });

    // Sending data to clients
    clientContainersIteration.forEach((iteration, clientId) => {
      onCarousel(iteration, delay, clientId);
    });
    

    if (carouselInfo.displayNextCarouselTimeout) clearTimeout(carouselInfo.displayNextCarouselTimeout);

    // saved date of next carousel will be displayed
    const updateIndexes = () => {
      // update local indexes
      carouselInfo.displayNextCarousel = undefined;
      carouselInfo.carousels.forEach((group) => {
        group.groups.forEach((container) => {
          // next carousel displayed
          if(container.nextRoundIndex !== undefined) container.roundIndex = container.nextRoundIndex;
          container.nextRoundIndex = undefined;
        });
      });
    };
    
    if (delay > 0) {
      carouselInfo.displayNextCarousel = new Date(new Date().getTime() + delay);
      // update indexes after delay
      carouselInfo.displayNextCarouselTimeout = setTimeout(updateIndexes, delay);
    } else {
      // update indexes now
      updateIndexes();
      carouselInfo.displayNextCarousel = undefined;
      carouselInfo.displayNextCarouselTimeout = undefined;
    }
  }, [getClientContainersMappedByCarouselAndGroup, onCarousel]);

  const update = useCallback(debounce(() => {
    // Not running
    if (!carouselIntervalRef.current) return;

    generateCarouselInfo();
    const carouselInfo = carouselInfoRef.current;
    if (!carouselInfo) return;

    const containersByCarouselAndGroup = getClientContainersMappedByCarouselAndGroup();

    // Clients will receive their respective data,
    // Since multiple containers can exist per client, we gather all the data first
    const clientContainersIteration: Map<string, ClientContainersIteration> = new Map();
    const getOrCreate = (clientId: string): ClientContainersIteration => {
      let container = clientContainersIteration.get(clientId);
      if (!container) {
        container = { containers: [] };
        clientContainersIteration.set(clientId, container);
      }
      return container;
    };

    carouselInfo.carousels.forEach((carousel) => {
      const containersByGroup = containersByCarouselAndGroup.get(carousel.id);

      // Session Mode
      carousel.groups.forEach((group) => {
        const containers = containersByGroup?.get(group.id);

        if (!group.rounds.length) {
          containers?.forEach((container) => {
            const iteration = getOrCreate(container.client.id);
            iteration.containers.push({
              id: container.id, // column id
              sessions: [],
              totalSessions: 0,
              page: 0,
              pageCount: 0,
              carouselId: container.carouselId,
              groupId: container.groupId,
            });
          });
          return;
        }

        // Setting current round
        group.roundIndex = getValidRoundIndex(group, group.roundIndex);
        
        containers?.forEach((container) => {
          // Session Mode
          const currCapacity = Math.min(group.capacity, container.capacity);
          const pageInfo = convertToPageInfo(group.roundIndex, group.rounds);

          const iteration = getOrCreate(container.client.id);
          iteration.containers.push({
            id: container.id, // column id
            sessions: pageInfo.round ? pageInfo.round.items.slice(0, currCapacity).map((s) => s.media) : [],
            totalSessions: carousel.connectedMedia.length,
            page: pageInfo.page,
            pageCount: pageInfo.pageCount,
            carouselId: container.carouselId,
            groupId: container.groupId,
          });
        });
      });

    });

    // Sending data to clients
    clientContainersIteration.forEach((iteration, clientId) => {
      onCarousel(iteration, 0, clientId);
    });
    
    /**
     * 
     * Bug explaination when receiving a lot of fast updates :
     * - setNextCarousel was called from the main setInterval with page=N+1 (next page)
     * - then right away, here postDisplayCarousel was called with delay=0, forcing slaves to update back to page=N
     *   -> roundIndex still pointing to page N, because the roundIndex=N+1 is done at the end of the setNextCarousel timeout
     * - then here setNextCarousel was not called again because of a condition that delay must be > 0
     * 
     * Fix :
     * - here, setNextCarousel is now called again, with delay=0, immediatly setting the next page again
     */

    // refresh next carousel if already scheduled
    if (carouselInfo.displayNextCarousel) {
      const now = new Date();
      const delay = carouselInfo.displayNextCarousel.getTime() - now.getTime();
      setNext("increment", Math.max(0, delay));
    }
  }, 500, { leading: true, maxWait: 500 }), [generateCarouselInfo, getClientContainersMappedByCarouselAndGroup, onCarousel, setNext]);
  
  const stop = () => {
    if (carouselIntervalRef.current) clearInterval(carouselIntervalRef.current);
    if (carouselInfoRef.current?.displayNextCarouselTimeout) clearTimeout(carouselInfoRef.current.displayNextCarouselTimeout);
    carouselIntervalRef.current = undefined;
    carouselInfoRef.current = undefined;
  };
  
  const start = () => {
    stop();
    generateCarouselInfo();
    setNext('restart', 0);
    carouselIntervalRef.current = setInterval(() => {
      setNext('increment', settings?.carousel?.prelaodDelay ?? DEFAULT_CAROUSEL_DELAY);
    }, settings?.carousel?.interval ?? DEFAULT_CAROUSEL_INTERVAL);
  };

  const isRunning = () => {
    return carouselIntervalRef.current !== undefined;
  };

  return {
    update,
    start,
    stop,
    isRunning,
  };

}